/**
 * ChatTaskLiveCard — inline live progress card embedded in the chat thread.
 *
 * Reads from the same React Query cache as Mission Control via
 * `useTaskDetail`, so WebSocket updates touched by `useTaskEventStream`
 * propagate automatically (single source of truth).
 *
 * Phase 3 - Task 15.
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTaskDetail } from '@/hooks/useTaskDetail';
import { patchControlSignal, publishCr, type ControlSignal } from '@/api/tasks';
import { AGENTS, type AgentId } from '@/tokens/colors';

const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'COMPLETED',
  'FAILED',
  'ABORTED',
]);

export interface ChatTaskLiveCardProps {
  readonly runId: string;
}

export function ChatTaskLiveCard({ runId }: ChatTaskLiveCardProps): ReactElement {
  const { data } = useTaskDetail(runId);
  const queryClient = useQueryClient();
  const publish = useMutation({
    mutationFn: () => publishCr({ runId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', runId] });
    },
  });
  const control = useMutation({
    mutationFn: (signal: ControlSignal) => patchControlSignal({ runId, signal }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', runId] });
    },
  });

  if (!data) {
    return (
      <div
        data-testid="chat-task-live-card-skeleton"
        className="h-24 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900/40"
      />
    );
  }

  const identity = AGENTS[data.agent as AgentId] ?? AGENTS.developer;
  const stripe = identity.light;
  const tools = (data.subtypeData as { toolCalls?: number } | undefined)?.toolCalls ?? 0;
  const pct = Math.max(0, Math.min(100, data.progressPct ?? 0));
  const cr = (data.subtypeData as { cr?: { state?: string } } | undefined)?.cr;
  const showPublish = cr?.state === 'draft';

  return (
    <article
      data-runid={runId}
      data-agent={data.agent}
      data-status={data.status}
      className="rounded-lg border border-zinc-700/50 bg-zinc-900/60 p-3"
    >
      <header className="flex items-center gap-2">
        <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: stripe }} />
        <span className="text-xs uppercase tracking-wide text-zinc-400">
          Full-Auto · {data.status}
        </span>
      </header>
      <h3 className="mt-1 truncate font-medium text-zinc-100">{data.title}</h3>
      <p className="mt-1 text-sm text-zinc-400">
        Phase: <span className="text-zinc-200">{data.currentStep ?? '—'}</span>
        {'  ·  '}
        <span className="text-zinc-200">{tools} tool calls</span>
      </p>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        className="mt-2 h-1.5 w-full overflow-hidden rounded bg-zinc-800"
      >
        <div className="h-full rounded bg-sky-500" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1 text-right text-xs text-zinc-500">{pct}%</p>
      <footer className="mt-2 flex items-center justify-between">
        <Link to={`/tasks?runId=${runId}`} className="text-sm text-sky-400 hover:underline">
          View in Mission Control →
        </Link>
        <div className="flex gap-2">
          {/* Phase 4 Task 34: PAUSE + ABORT visible only while running. */}
          {!TERMINAL_STATUSES.has(data.status) && data.status !== 'PAUSED' && (
            <>
              <button
                type="button"
                onClick={() => control.mutate('PAUSE')}
                disabled={control.isPending}
                className="rounded border border-zinc-700 px-2 py-0.5 text-xs text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
              >
                Pause
              </button>
              <button
                type="button"
                onClick={() => control.mutate('ABORT')}
                disabled={control.isPending}
                className="rounded border border-rose-700/50 px-2 py-0.5 text-xs text-rose-300 hover:bg-rose-950/30 disabled:opacity-50"
              >
                Abort
              </button>
            </>
          )}
          {/* Phase 4 Task 34: RESUME visible only while paused. */}
          {data.status === 'PAUSED' && (
            <button
              type="button"
              onClick={() => control.mutate('RESUME')}
              disabled={control.isPending}
              className="rounded border border-emerald-500 px-2 py-0.5 text-xs text-emerald-300 hover:bg-emerald-950/30 disabled:opacity-50"
            >
              Resume
            </button>
          )}
          {showPublish && !TERMINAL_STATUSES.has(data.status)
            ? null
            : showPublish && data.status === 'COMPLETED' && (
                <button
                  type="button"
                  onClick={() => publish.mutate()}
                  disabled={publish.isPending}
                  className="rounded bg-emerald-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {publish.isPending ? 'Publishing…' : 'Publish CR'}
                </button>
              )}
        </div>
      </footer>
      {publish.isError && (
        <p className="mt-1 text-xs text-rose-400">{(publish.error as Error).message}</p>
      )}
      {control.isError && (
        <p className="mt-1 text-xs text-rose-400">{(control.error as Error).message}</p>
      )}
    </article>
  );
}

export default ChatTaskLiveCard;
